import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Ocurrió un error. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#ffffff]">
      <div className="hidden lg:flex flex-col justify-between bg-[#333333] text-white p-12">
        <img src="/logo.png" alt="Brillo Finance" className="h-10 w-auto object-contain bg-white p-1.5 rounded-sm" />
        <div>
          <h2 className="font-display text-4xl font-bold tracking-tighter leading-tight">Panel de<br />administración</h2>
          <p className="text-white/60 mt-4 max-w-sm">Consulta los clientes precalificados, su información de contacto y su perfil de financiamiento en tiempo real.</p>
        </div>
        <div className="text-xs text-white/40">Guadalajara, Jalisco · Brillo Finance 2026</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <a href="/" className="inline-flex items-center gap-1.5 text-sm text-[#52525B] hover:text-[#333333] mb-8"><ArrowLeft className="w-4 h-4" /> Volver al sitio</a>
          <div className="w-12 h-12 bg-[#04b0ba] grid place-items-center mb-5"><Lock className="w-5 h-5 text-white" /></div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Iniciar sesión</h1>
          <p className="text-sm text-[#52525B] mt-1">Accede a tu panel de leads.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="label-caps block mb-1.5">Correo</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full px-4 py-3 border border-[#E4E4E7] bg-white outline-none focus:border-[#04b0ba] transition-colors" placeholder="admin@brillofinance.com" />
            </div>
            <div>
              <label className="label-caps block mb-1.5">Contraseña</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="w-full px-4 py-3 border border-[#E4E4E7] bg-white outline-none focus:border-[#04b0ba] transition-colors" placeholder="••••••••" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-500">{error}</p>}
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#04b0ba] hover:bg-[#038f98] text-white font-semibold py-3 transition-colors disabled:opacity-60">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Entrando…</> : "Entrar"}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
